import { Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getProtocolKeyCopy } from "@/lib/protocol-key-copy";
import {
  useAddPhoto,
  useProtocolPhotos,
  useRemovePhoto,
} from "@/hooks/useProtocol";
import { PhotoGrid } from "@/components/protocol/PhotoGrid";
import { MIN_ROOM_PHOTOS } from "@/lib/protocol";
import { formatMsg, type ProtocolCopy } from "@/lib/protocol-copy";

const TOTAL_CAP = 5;

export function PhotosStep({
  protocolId,
  t,
}: {
  protocolId: string;
  t: ProtocolCopy;
}) {
  const { language, tDict } = useLanguage();
  const keyCopy = getProtocolKeyCopy(language, tDict);
  const { data: photos = [], isLoading } = useProtocolPhotos(protocolId);
  const add = useAddPhoto(protocolId);
  const remove = useRemovePhoto(protocolId);
  const roomPhotos = photos.filter((p) => p.category === "room");

  return (
    <div>
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between md:gap-6">
        <div>
          <h2 className="text-[26px] font-bold leading-tight text-neutral-900 md:text-3xl">
            {t.photosTitle}
          </h2>
          <p className="mt-1 text-sm text-neutral-500 md:max-w-xl">
            {formatMsg(t.photosSubtitle, { min: MIN_ROOM_PHOTOS })}
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm md:flex-col md:items-end md:gap-1">
          <span className="font-semibold text-neutral-900">
            {formatMsg(t.photosCount, { n: roomPhotos.length, max: TOTAL_CAP })}
          </span>
          <span className="text-neutral-500">
            {formatMsg(t.photosMin, { n: MIN_ROOM_PHOTOS })}
          </span>
        </div>
      </div>
      <div className="mt-5 md:mt-8" />

      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-neutral-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <PhotoGrid
          photos={roomPhotos}
          category="room"
          labels={keyCopy}
          onAdd={(file, ordering) =>
            add.mutateAsync({ file, category: "room", ordering })
          }
          onRemove={(photo) => remove.mutateAsync(photo)}
          uploading={add.isPending}
          max={TOTAL_CAP}
        />
      )}
    </div>
  );
}

export function isPhotosStepComplete(roomPhotosLength: number): boolean {
  return roomPhotosLength >= MIN_ROOM_PHOTOS;
}
